import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, User, KeyRound, Copy, Check, Loader2, ShieldQuestion, LogIn, UserPlus } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const MODES = { LOGIN: "login", SIGNUP: "signup", RECOVERY: "recovery" };

export default function AuthModal({ open, initialMode = MODES.SIGNUP, onClose }) {
  const { signup, login, recover } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showRecovery, setShowRecovery] = useState(null); // {code, isNew}
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    if (open) {
      setMode(initialMode);
      setError("");
      setShowRecovery(null);
      setCopied(false);
    }
  }, [open, initialMode]);

  if (!open) return null;

  const reset = () => {
    setUsername("");
    setPassword("");
    setRecoveryCode("");
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === MODES.SIGNUP) {
        const r = await signup(username, password);
        if (r?.recovery_code) {
          setShowRecovery({ code: r.recovery_code, isNew: false });
        }
        toast.success(`Welcome, ${r.user.username}`);
      } else if (mode === MODES.LOGIN) {
        const r = await login(username, password);
        toast.success(`Signed in as ${r.user.username}`);
        onClose?.();
      } else if (mode === MODES.RECOVERY) {
        const r = await recover(username, recoveryCode, password);
        setShowRecovery({ code: r.new_recovery_code, isNew: true });
        toast.success("Password reset. Save your new recovery code.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = () => {
    if (!showRecovery?.code) return;
    navigator.clipboard.writeText(showRecovery.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const finishRecovery = () => {
    setShowRecovery(null);
    reset();
    onClose?.();
  };

  return createPortal(
    <div
      data-testid="auth-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm overflow-y-auto flex items-start sm:items-center justify-center p-4 py-10 no-print"
    >
      <div className="w-full max-w-md bg-[#0a0a0a] border-2 border-purple-500 shadow-[0_0_60px_-20px_rgba(168,85,247,0.6)]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-purple-500/30">
          <div className="font-mono text-[10px] tracking-[0.3em] text-purple-400 flex items-center gap-2">
            <ShieldQuestion size={11} /> V. JONES VAULT // ACCESS
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="auth-modal-close"
            className="text-zinc-500 hover:text-white"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {showRecovery ? (
          // Recovery-code reveal screen
          <div className="px-5 py-6" data-testid="recovery-reveal">
            <div className="font-mono text-[10px] tracking-[0.25em] text-purple-400 mb-2">
              {showRecovery.isNew ? "// NEW RECOVERY CODE — SAVE IT" : "// YOUR RECOVERY CODE — SAVE IT NOW"}
            </div>
            <h3 className="font-heading font-black uppercase tracking-tight text-2xl text-white mb-3">
              This is the only way to recover your account.
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed mb-4">
              We don&apos;t collect emails. If you forget your password, this code is your only path back.
              Save it to a password manager, paper, or your notes app — <span className="text-[#ff3b30]">we will never show it again</span>.
            </p>
            <div className="border-2 border-purple-500/60 bg-purple-500/5 p-4 mb-4">
              <div className="font-mono text-2xl tracking-[0.2em] text-purple-300 text-center select-all break-all">
                {showRecovery.code}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={copyCode}
                data-testid="copy-recovery-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 border border-purple-500/50 text-purple-300 hover:bg-purple-500/10 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? "Copied" : "Copy code"}
              </button>
              <button
                type="button"
                onClick={finishRecovery}
                data-testid="recovery-confirm-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-purple-500 text-[#0a0a0a] hover:bg-white font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
              >
                I&apos;ve saved it
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Mode tabs */}
            <div className="grid grid-cols-3 border-b border-white/10">
              {[
                { id: MODES.SIGNUP, label: "Sign up", Icon: UserPlus },
                { id: MODES.LOGIN, label: "Sign in", Icon: LogIn },
                { id: MODES.RECOVERY, label: "Recover", Icon: KeyRound },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { setMode(t.id); reset(); }}
                  data-testid={`auth-tab-${t.id}`}
                  className={`px-3 py-3 font-mono text-[10px] tracking-[0.2em] uppercase border-r border-white/10 last:border-r-0 flex items-center justify-center gap-2 transition-colors ${
                    mode === t.id
                      ? "text-purple-300 bg-purple-500/10"
                      : "text-zinc-500 hover:text-white"
                  }`}
                >
                  <t.Icon size={11} /> {t.label}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="px-5 py-5 space-y-4" data-testid="auth-form">
              <div>
                <label className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 flex items-center gap-2 mb-2">
                  <User size={11} /> USERNAME
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase())}
                  required
                  minLength={3}
                  maxLength={30}
                  autoComplete="username"
                  data-testid="auth-username"
                  placeholder="your_handle"
                  className="w-full bg-transparent border border-white/15 px-3 py-2 font-mono text-sm text-white placeholder-zinc-600 focus:border-purple-500 focus:outline-none"
                />
                {mode === MODES.SIGNUP && (
                  <p className="font-mono text-[9px] tracking-wider text-zinc-600 mt-1">
                    3–30 chars · lowercase letters, numbers, underscores
                  </p>
                )}
              </div>

              {mode === MODES.RECOVERY && (
                <div>
                  <label className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 flex items-center gap-2 mb-2">
                    <KeyRound size={11} /> RECOVERY CODE
                  </label>
                  <input
                    type="text"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                    required
                    data-testid="auth-recovery-code"
                    placeholder="XXXX-XXXX-XXXX"
                    className="w-full bg-transparent border border-white/15 px-3 py-2 font-mono text-sm text-white placeholder-zinc-600 focus:border-purple-500 focus:outline-none uppercase"
                  />
                </div>
              )}

              <div>
                <label className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 flex items-center gap-2 mb-2">
                  <KeyRound size={11} /> {mode === MODES.RECOVERY ? "NEW PASSWORD" : "PASSWORD"}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  maxLength={128}
                  autoComplete={mode === MODES.LOGIN ? "current-password" : "new-password"}
                  data-testid="auth-password"
                  placeholder="at least 6 characters"
                  className="w-full bg-transparent border border-white/15 px-3 py-2 font-mono text-sm text-white placeholder-zinc-600 focus:border-purple-500 focus:outline-none"
                />
              </div>

              {error && (
                <div data-testid="auth-error" className="font-mono text-[10px] tracking-wider text-[#ff3b30] border border-[#ff3b30]/40 bg-[#ff3b30]/5 px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy || !username.trim() || !password.trim() || (mode === MODES.RECOVERY && !recoveryCode.trim())}
                data-testid="auth-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-purple-500 text-[#0a0a0a] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                {mode === MODES.SIGNUP && "Create account"}
                {mode === MODES.LOGIN && "Sign in"}
                {mode === MODES.RECOVERY && "Reset password"}
              </button>

              <p className="font-mono text-[9px] tracking-wider text-zinc-600 text-center leading-relaxed">
                {mode === MODES.SIGNUP
                  ? "We DO NOT collect email or phone. Just username + password."
                  : mode === MODES.LOGIN
                  ? "Anonymous browsing still works without an account."
                  : "Lost your password? Enter your username + recovery code to set a new one."}
              </p>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

AuthModal.MODES = MODES;
